import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, Clock, MessageSquare, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Reveal, { SectionHeading } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = ["Website", "Business cards", "Leaflets & flyers", "A bit of everything", "Something else"];

const PROMISES = [
  { icon: Clock, text: "Replies within one working day" },
  { icon: MessageSquare, text: "A no-obligation quote, straight up" },
  { icon: Sparkles, text: "Designed from scratch — never templates" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", service: SERVICES[0], message: "" });
  const [state, setState] = useState("idle");

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (state === "done") setState("idle");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    try {
      await axios.post(`${API}/enquiries`, form, { timeout: 15000 });
      toast.success("Enquiry received — we'll reply within one working day.", {
        description: "Talk soon. — Southline Creative",
      });
      setState("done");
    } catch (err) {
      toast.error("Something went wrong — please try again.");
      setState("idle");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(212,175,55,0.09),transparent_55%)]" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
        <div>
          <SectionHeading
            index="04"
            eyebrow="Start a project"
            title={<>Tell us what you're <span className="italic text-gold">dreaming up.</span></>}
          />
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-md text-base text-cream/60 font-light leading-relaxed">
              Whether it's a full website or a hundred business cards, every
              project starts with a conversation. Tell us where your business
              is going — we'll make sure it looks the part when it gets there.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <ul className="mt-12 space-y-6">
              {PROMISES.map((p) => (
                <li key={p.text} className="flex items-center gap-4 text-sm text-cream/75">
                  <span className="flex items-center justify-center w-10 h-10 border border-gold/30 text-gold shrink-0">
                    <p.icon className="w-4 h-4" />
                  </span>
                  {p.text}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <form
            data-testid="quote-form"
            onSubmit={onSubmit}
            className="border border-white/10 bg-surface/70 backdrop-blur-sm p-8 md:p-12"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <label className="block">
                <span className="eyebrow text-[10px]">Your name</span>
                <input
                  data-testid="quote-input-name"
                  required
                  maxLength={120}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Alex Morgan"
                  className="input-luxe mt-2"
                />
              </label>
              <label className="block">
                <span className="eyebrow text-[10px]">Email</span>
                <input
                  data-testid="quote-input-email"
                  required
                  type="email"
                  maxLength={200}
                  value={form.email}
                  onChange={set("email")}
                  placeholder="alex@business.com"
                  className="input-luxe mt-2"
                />
              </label>
            </div>

            <label className="block mt-8">
              <span className="eyebrow text-[10px]">What do you need?</span>
              <select
                data-testid="quote-input-service"
                value={form.service}
                onChange={set("service")}
                className="input-luxe mt-2 appearance-none cursor-pointer bg-[#0d0d0d] bg-none"
              >
                {SERVICES.map((s) => (
                  <option key={s} value={s} className="bg-[#0d0d0d] text-cream">
                    {s}
                  </option>
                ))}
              </select>
            </label>

            <label className="block mt-8">
              <span className="eyebrow text-[10px]">Tell us about the project</span>
              <textarea
                data-testid="quote-input-message"
                required
                rows={4}
                maxLength={4000}
                value={form.message}
                onChange={set("message")}
                placeholder="A new website for our restaurant, plus menus for the bar…"
                className="input-luxe mt-2 resize-none"
              />
            </label>

            <motion.button
              data-testid="quote-submit-button"
              type="submit"
              disabled={state === "sending"}
              whileHover={{ scale: state === "sending" ? 1 : 1.015 }}
              whileTap={{ scale: state === "sending" ? 1 : 0.985 }}
              className="btn-shine mt-10 w-full bg-gold text-ink py-4 text-sm uppercase tracking-[0.25em] font-medium hover:bg-gold-soft transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {state === "sending" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                </>
              ) : state === "done" ? (
                "Sent — talk soon"
              ) : (
                "Request a quote"
              )}
            </motion.button>
            <p className="mt-5 text-[11px] text-cream/35 text-center tracking-wide">
              No spam, no pressure — just a considered reply and a fair quote.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
